// src/utils/helpers.js

import { STATUS_VALUES, STATUS_HEX, DATE_PATTERNS } from '../constants';

export const cleanValue = (value) => String(value || '').trim();

export const normalizeKey = (value) => {
  return String(value || '')
    .toLowerCase()
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const hasMeaningfulValue = (value) => {
  const v = normalizeKey(value);
  const meaningless = ['', '-', 'na', 'n/a', 'null', 'undefined', 'unknown'];
  return !meaningless.includes(v);
};

export const toNumber = (value) => {
  const cleaned = String(value || '').replace(/,/g, '').trim();
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : 0;
};

export const pct = (numerator, denominator) => {
  return denominator > 0 ? Math.round((numerator / denominator) * 100) : 0;
};

export const shortText = (value, maxLength = 35) => {
  const text = cleanValue(value);
  if (!text) return '—';
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

export const normaliseDate = (raw) => {
  if (!raw) return null;

  const str = String(raw).trim();
  if (!str) return null;

  if (DATE_PATTERNS.iso.test(str)) {
    return str;
  }

  const date = new Date(str);

  if (!isNaN(date.getTime())) {
    return date.toISOString().slice(0, 10);
  }

  return null;
};

export const looksLikeTimestampValue = (value) => {
  const v = String(value || '').trim();

  if (!v) return false;

  return (
    DATE_PATTERNS.timestamp.test(v) ||
    DATE_PATTERNS.usShort.test(v) ||
    DATE_PATTERNS.time.test(v) ||
    /\btimestamp\b/i.test(v) ||
    /\bupdated\s*at\b/i.test(v) ||
    /\blast\s*updated\b/i.test(v)
  );
};

export const getStatusColor = (value) => {
  const v = normalizeKey(value);

  if (!v) return 'slate';

  // Important: check "no damage" before "damage"
  if (
    v.includes('no damage') ||
    v.includes('not damaged') ||
    v.includes('no damaged') ||
    v.includes('without damage') ||
    v.includes('good condition')
  ) {
    return 'success';
  }

  if (
    v.includes('damage') ||
    v.includes('damaged') ||
    v.includes('fault') ||
    v.includes('faulty') ||
    v.includes('failed') ||
    v.includes('failure') ||
    v.includes('defect') ||
    v.includes('defective') ||
    v.includes('broken')
  ) {
    return 'danger';
  }

  if (STATUS_VALUES.positive.some((term) => v.includes(term))) {
    return 'success';
  }

  if (STATUS_VALUES.negative.some((term) => v.includes(term))) {
    return 'danger';
  }

  return 'slate';
};

export const getStatusHex = (value) => {
  const key = getStatusColor(value);
  return STATUS_HEX[key] || STATUS_HEX.slate;
};

/** DOA sheet: display "Remarks" instead of legacy "Last Updated" header label. */
export const formatDoaHeader = (header) => {
  const normalized = normalizeHeaderName(header);
  if (normalized.toLowerCase() === 'last updated') {
    return 'Remarks';
  }
  return normalized;
};

export const isPassValue = (value) => {
  const v = normalizeKey(value);

  if (!v) return false;

  if (
    v.includes('no damage') ||
    v.includes('not damaged') ||
    v.includes('no damaged') ||
    v.includes('without damage') ||
    v.includes('good condition')
  ) {
    return true;
  }

  if (
    v.includes('damage') ||
    v.includes('damaged') ||
    v.includes('fault') ||
    v.includes('faulty') ||
    v.includes('failed') ||
    v.includes('failure') ||
    v.includes('defect') ||
    v.includes('defective') ||
    v.includes('broken')
  ) {
    return false;
  }

  return STATUS_VALUES.positive.some((term) => v.includes(term));
};

export const isFailValue = (value) => {
  const v = normalizeKey(value);

  if (!v) return false;

  if (
    v.includes('no damage') ||
    v.includes('not damaged') ||
    v.includes('no damaged') ||
    v.includes('without damage') ||
    v.includes('good condition')
  ) {
    return false;
  }

  if (
    v.includes('damage') ||
    v.includes('damaged') ||
    v.includes('fault') ||
    v.includes('faulty') ||
    v.includes('failed') ||
    v.includes('failure') ||
    v.includes('defect') ||
    v.includes('defective') ||
    v.includes('broken')
  ) {
    return true;
  }

  return STATUS_VALUES.negative.some((term) => v.includes(term));
};

export const findHeader = (headers, patterns) => {
  return headers.find((header) =>
    patterns.some((pattern) => pattern.test(String(header || '')))
  );
};

export const normalizeHeaderName = (header) => {
  return String(header || '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};